import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface BusinessNameInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { business_name?: string | null };
  onChange?: (val: { business_name: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  // Legacy pattern
  initialData?: { business_name?: string };
  onDataChange?: (data: { business_name: string }) => void;
}

function validateBusinessName(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Business name is required';
  if (trimmed.length < 2) return 'Business name must be at least 2 characters';
  return '';
}

const BusinessNameInput: React.FC<BusinessNameInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const businessName = (value?.business_name ?? initialData?.business_name ?? '') || '';

  const [touched, setTouched] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const name = (valueRef.current?.business_name ?? '') || '';
      return validateBusinessName(name) === '';
    });
  }, [registerSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange?.({ business_name: val });
    onDataChange?.({ business_name: val });
  }, [onChange, onDataChange]);

  const error = touched ? validateBusinessName(businessName) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="business_name" className="text-gray-500 font-medium">
        Business Name <span className="text-red-500">*</span>
      </Label>
      <Input
        id="business_name"
        type="text"
        value={businessName}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="Enter business name"
        disabled={disabled}
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${error ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default BusinessNameInput;
